import { cloneValue, collectionKey, isRecord } from "./preset-internals.js";

export const PRESET_DATABASE_NAME = "fishystuff-user-presets";
export const PRESET_DATABASE_VERSION = 1;

const STORES = Object.freeze({
  workspace: "workspaceCollections",
  saved: "savedPresetCollections",
  migration: "presetMigrations",
  quarantine: "presetQuarantine",
});

function providerError(code, cause = null) {
  const error = new Error(code, cause ? { cause } : undefined);
  error.code = code;
  return error;
}

function requestResult(request, code) {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(providerError(code, request.error)),
      { once: true },
    );
  });
}

function transactionDone(transaction, code) {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener(
      "abort",
      () => reject(providerError(code, transaction.error)),
      { once: true },
    );
    transaction.addEventListener(
      "error",
      () => reject(providerError(code, transaction.error)),
      { once: true },
    );
  });
}

function openDatabase(indexedDBRef, databaseName) {
  if (!indexedDBRef || typeof indexedDBRef.open !== "function") {
    return Promise.reject(providerError("indexeddb-unavailable"));
  }
  return new Promise((resolve, reject) => {
    let request;
    try {
      request = indexedDBRef.open(databaseName, PRESET_DATABASE_VERSION);
    } catch (error) {
      reject(providerError("indexeddb-open-failed", error));
      return;
    }
    request.addEventListener("upgradeneeded", () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORES.workspace)) {
        database.createObjectStore(STORES.workspace, { keyPath: "collectionKey" });
      }
      if (!database.objectStoreNames.contains(STORES.saved)) {
        database.createObjectStore(STORES.saved, { keyPath: "collectionKey" });
      }
      if (!database.objectStoreNames.contains(STORES.migration)) {
        database.createObjectStore(STORES.migration, { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains(STORES.quarantine)) {
        const store = database.createObjectStore(STORES.quarantine, { keyPath: "id" });
        store.createIndex("collectionKey", "collectionKey", { unique: false });
      }
    });
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(providerError("indexeddb-open-failed", request.error)),
      { once: true },
    );
    request.addEventListener(
      "blocked",
      () => reject(providerError("indexeddb-open-blocked")),
      { once: true },
    );
  });
}

function exactMutation(value, label) {
  if (!isRecord(value)) {
    throw new TypeError(`${label} reducer must return an object.`);
  }
  const fields = Object.keys(value).sort();
  if (
    fields.length !== 2
    || fields[0] !== "record"
    || fields[1] !== "result"
  ) {
    throw new TypeError(`${label} reducer must return exactly record and result.`);
  }
  if (value.record !== null && !isRecord(value.record)) {
    throw new TypeError(`${label} record must be an object or null.`);
  }
  return value;
}

export function createIndexedDbPresetProvider({
  indexedDB: indexedDBRef = globalThis.indexedDB,
  databaseName = PRESET_DATABASE_NAME,
} = {}) {
  let databasePromise = null;
  let disposed = false;

  function database() {
    if (disposed) {
      return Promise.reject(providerError("indexeddb-provider-disposed"));
    }
    if (!databasePromise) {
      databasePromise = openDatabase(indexedDBRef, databaseName).catch((error) => {
        databasePromise = null;
        throw error;
      });
    }
    return databasePromise;
  }

  async function read(storeName, key, code) {
    const db = await database();
    let transaction;
    try {
      transaction = db.transaction(storeName, "readonly");
    } catch (error) {
      throw providerError(code, error);
    }
    const done = transactionDone(transaction, code);
    const result = await requestResult(transaction.objectStore(storeName).get(key), code);
    await done;
    return result === undefined ? null : cloneValue(result);
  }

  async function mutate(storeName, key, reducer, code) {
    if (typeof reducer !== "function") {
      throw new TypeError("Preset provider reducer must be a function.");
    }
    const db = await database();
    let transaction;
    try {
      transaction = db.transaction(storeName, "readwrite");
    } catch (error) {
      throw providerError(code, error);
    }
    const done = transactionDone(transaction, code);
    const store = transaction.objectStore(storeName);
    try {
      const current = await requestResult(store.get(key), code);
      const mutation = exactMutation(
        reducer(current === undefined ? null : cloneValue(current)),
        "Preset provider",
      );
      if (mutation.record === null) {
        await requestResult(store.delete(key), code);
      } else {
        if (mutation.record.collectionKey !== key) {
          throw new TypeError("Preset provider record collection key mismatch.");
        }
        await requestResult(store.put(cloneValue(mutation.record)), code);
      }
      await done;
      return cloneValue(mutation.result);
    } catch (error) {
      try {
        transaction.abort();
      } catch (_abortError) {
        // The transaction may already have aborted because of the failed request.
      }
      try {
        await done;
      } catch (_transactionError) {
        // Preserve the domain/reducer error when one exists.
      }
      throw error;
    }
  }

  async function readWorkspace(rawKey) {
    const key = collectionKey(rawKey);
    return read(STORES.workspace, key, "workspace-read-failed");
  }

  async function mutateWorkspace(rawKey, reducer) {
    const key = collectionKey(rawKey);
    return mutate(STORES.workspace, key, reducer, "workspace-write-failed");
  }

  async function readSaved(rawKey) {
    const key = collectionKey(rawKey);
    return read(STORES.saved, key, "saved-presets-read-failed");
  }

  async function mutateSaved(rawKey, reducer) {
    const key = collectionKey(rawKey);
    return mutate(STORES.saved, key, reducer, "saved-presets-write-failed");
  }

  async function migrationReceipt(id) {
    return read(STORES.migration, id, "preset-migration-read-failed");
  }

  async function quarantineForCollection(rawKey) {
    const key = collectionKey(rawKey);
    const db = await database();
    let transaction;
    try {
      transaction = db.transaction(STORES.quarantine, "readonly");
    } catch (error) {
      throw providerError("preset-quarantine-read-failed", error);
    }
    const done = transactionDone(transaction, "preset-quarantine-read-failed");
    const index = transaction.objectStore(STORES.quarantine).index("collectionKey");
    const rows = await requestResult(index.getAll(key), "preset-quarantine-read-failed");
    await done;
    return cloneValue(rows);
  }

  async function atomicLegacyImport({
    workspaceRecords,
    savedRecords,
    quarantine,
    collisionQuarantine = [],
    collisionReceipt = null,
    receipt,
  }) {
    if (
      !Array.isArray(workspaceRecords)
      || !Array.isArray(savedRecords)
      || !Array.isArray(quarantine)
      || !Array.isArray(collisionQuarantine)
    ) {
      throw new TypeError("Legacy preset import records must be arrays.");
    }
    if (!isRecord(receipt) || typeof receipt.id !== "string" || !receipt.id) {
      throw new TypeError("Legacy preset import receipt is invalid.");
    }
    if (
      collisionReceipt !== null
      && (
        !isRecord(collisionReceipt)
        || collisionReceipt.id !== receipt.id
      )
    ) {
      throw new TypeError("Legacy preset collision receipt is invalid.");
    }
    const db = await database();
    const names = [STORES.workspace, STORES.saved, STORES.migration, STORES.quarantine];
    let transaction;
    try {
      transaction = db.transaction(names, "readwrite");
    } catch (error) {
      throw providerError("preset-migration-write-failed", error);
    }
    const done = transactionDone(transaction, "preset-migration-write-failed");
    try {
      const migrationStore = transaction.objectStore(STORES.migration);
      const existing = await requestResult(
        migrationStore.get(receipt.id),
        "preset-migration-write-failed",
      );
      if (existing !== undefined) {
        await done;
        return { imported: false, receipt: cloneValue(existing) };
      }

      const workspaceStore = transaction.objectStore(STORES.workspace);
      const savedStore = transaction.objectStore(STORES.saved);
      const quarantineStore = transaction.objectStore(STORES.quarantine);
      const collisionKeys = new Set();
      for (const record of workspaceRecords) {
        const existingRecord = await requestResult(
          workspaceStore.get(record.collectionKey),
          "preset-migration-write-failed",
        );
        if (existingRecord !== undefined) {
          collisionKeys.add(record.collectionKey);
        }
      }
      for (const record of savedRecords) {
        const existingRecord = await requestResult(
          savedStore.get(record.collectionKey),
          "preset-migration-write-failed",
        );
        if (existingRecord !== undefined) {
          collisionKeys.add(record.collectionKey);
        }
      }
      if (collisionKeys.size > 0) {
        for (const row of collisionQuarantine) {
          if (collisionKeys.has(row.collectionKey)) {
            await requestResult(
              quarantineStore.put(cloneValue(row)),
              "preset-migration-write-failed",
            );
          }
        }
        if (collisionReceipt) {
          await requestResult(
            migrationStore.put(cloneValue(collisionReceipt)),
            "preset-migration-write-failed",
          );
        }
        await done;
        return {
          imported: false,
          receipt: cloneValue(collisionReceipt),
          collision: { collectionKeys: Array.from(collisionKeys).sort() },
        };
      }
      for (const record of workspaceRecords) {
        await requestResult(workspaceStore.put(cloneValue(record)), "preset-migration-write-failed");
      }
      for (const record of savedRecords) {
        await requestResult(savedStore.put(cloneValue(record)), "preset-migration-write-failed");
      }
      for (const row of quarantine) {
        await requestResult(quarantineStore.put(cloneValue(row)), "preset-migration-write-failed");
      }
      await requestResult(migrationStore.put(cloneValue(receipt)), "preset-migration-write-failed");
      await done;
      return { imported: true, receipt: cloneValue(receipt) };
    } catch (error) {
      try {
        transaction.abort();
      } catch (_abortError) {
        // A failed request may have already aborted the transaction.
      }
      try {
        await done;
      } catch (_transactionError) {
        // Preserve the original failure.
      }
      throw error;
    }
  }

  async function clearAll() {
    const db = await database();
    const names = Object.values(STORES);
    let transaction;
    try {
      transaction = db.transaction(names, "readwrite");
    } catch (error) {
      throw providerError("preset-reset-failed", error);
    }
    const done = transactionDone(transaction, "preset-reset-failed");
    for (const name of names) {
      await requestResult(transaction.objectStore(name).clear(), "preset-reset-failed");
    }
    await done;
  }

  async function dispose() {
    disposed = true;
    if (databasePromise) {
      try {
        const db = await databasePromise;
        db.close();
      } catch (_error) {
        // A failed open has no database handle to close.
      }
    }
    databasePromise = null;
  }

  return Object.freeze({
    readWorkspace,
    mutateWorkspace,
    readSaved,
    mutateSaved,
    migrationReceipt,
    quarantineForCollection,
    atomicLegacyImport,
    clearAll,
    dispose,
  });
}
